/**
 * Self-hosted Google Fonts URL builders.
 *
 * These replace Google Fonts CSS URLs with Joy-hosted equivalents.
 * Each font family has one index.css containing all @font-face rules
 * for all weights, styles, and subsets. The browser selects which
 * .woff2 binaries to download via font-weight/font-style matching
 * and unicode-range.
 *
 * Assets are published by the mirror script: scripts/font-mirror/
 * CDN path: https://withjoy.com/assets/public/fonts/google/{slug}/
 */
import { SELF_HOSTED_FONTS_BASE } from '@shared/utils/urls';
import { FONT_WEIGHTS_CATALOG } from './selfHostedFonts.catalog';

/**
 * Known font family name inconsistencies between Joy's database and Google Fonts.
 * Must stay in sync with scripts/font-mirror/normalize.ts.
 */
const KNOWN_ALIASES: Readonly<Record<string, string>> = {
  UnifrakturCook: 'UnifrakturMaguntia',
  SirinStencil: 'Sirin Stencil',
  'Playwrite US Traditional': 'Playwrite USA Traditional',
  Thasadtih: 'Thasadith',
};

/**
 * Normalize a font family name by applying known aliases.
 * The mirrored catalog uses normalized names, but event data may carry legacy aliases.
 */
export const normalizeFontFamily = (family: string): string => {
  return KNOWN_ALIASES[family] ?? family;
};

const slugify = (family: string): string => {
  return normalizeFontFamily(family).toLowerCase().replace(/\s+/g, '-');
};

/**
 * Build the Joy CDN URL for a font family's index.css.
 * Normalizes legacy aliases automatically (e.g., "SirinStencil" → sirin-stencil/index.css).
 */
export const buildSelfHostedFontCssUrl = (family: string): string => {
  const slug = slugify(family);
  return `${SELF_HOSTED_FONTS_BASE}/${slug}/index.css`;
};

/**
 * The weights mirrored for a font family (of 400 and 700, the weights the loaders request), or
 * undefined for a family outside the catalog, such as a layout's own font.
 */
export const getMirroredFontWeights = (family: string): ReadonlyArray<number> | undefined => FONT_WEIGHTS_CATALOG[slugify(family)];

/**
 * Build direct .woff2 preload URLs for a font family's critical initial render.
 * Intersects the canonical preload set [400, 700] with the weights actually
 * mirrored for this family. Families not present in the catalog fall back to
 * 400 (the only weight every font is guaranteed to have).
 */
const PRELOAD_WEIGHTS: ReadonlyArray<number> = [400, 700];

export const buildCriticalPreloadUrls = (family: string): ReadonlyArray<string> => {
  const slug = slugify(family);
  const available = FONT_WEIGHTS_CATALOG[slug];
  const weights = available ? PRELOAD_WEIGHTS.filter(w => available.includes(w)) : [400];
  return weights.map(w => `${SELF_HOSTED_FONTS_BASE}/${slug}/${slug}-${w}-normal-latin.woff2`);
};
